import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the EmploymentTypeMasterContext
const HolidayMasterContext = createContext();

// Custom hook to use the EmploymentTypeMasterContext
export const useHolidayMaster = () => useContext(HolidayMasterContext);

// Provider component to wrap the app
export const HolidayMasterProvider = ({ children }) => {
  const [holidays, setHoliday] = useState([]);
  const API_URL = "https://hrmsnew.prepstripe.com/api/masters/public-holidays"; // Replace with your actual API URL
  const API_URL_BULK = "https://hrmsnew.prepstripe.com/api/masters/public-holidays/bulk"; 
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 15,
  });
  const [error, setError] = useState(null);
  const [isLoading, setLoading] = useState(false);
  

  // Fetch workplace master from the API on load
  useEffect(() => {
    fetchHolidays();
  }, [API_URL, token]);

  const fetchHolidays = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setHoliday(response?.data.data || {}); // Access `data.data` for actual holidays master
      setPagination({
        currentPage: response?.data.current_page || 1,
        totalPages: response?.data.last_page || 1,
        totalItems: response?.data.total || 0,
        perPage: response?.data.per_page || 15,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching holiday master:", error);
    } finally {
      setLoading(false);
    }
  };

  // Add a new workplace
  const addHoliday = async (newWorkplace) => {
    try {
      const response = await axios.post(API_URL, newWorkplace, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      // setHoliday((prev) => [...prev, response.data]);
      setHoliday((prev) => [response.data, ...prev]);

      return { success: true };
    } catch (error) {
      console.error("Error adding holiday:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to add holiday.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Update an existing workplace
  const updateHoliday = async (updatedHoliday) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedHoliday.id}`, updatedHoliday, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setHoliday((prev) =>
        prev.map((holiday) =>
            holiday.id === updatedHoliday.id ? { ...holiday, ...response.data } : holiday
        )
      );
      return { success: true };
    } catch (error) {
      console.error("Error updating holiday:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to update holiday.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Delete a employment-type
  const deleteHoliday = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setHoliday((prev) => prev.filter((workplace) => workplace.id !== id));
      return { success: true };
    } catch (error) {
      console.error("Error deleting holiday:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to delete holiday.");
      return { success: false, message: error.response?.data?.message };
    }
  };

  // Add a new workplace
  const createBulkHoliday = async (newWorkplaces) => {
    try {
        const response = await axios.post(API_URL_BULK, newWorkplaces, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        
        // Ensure the response contains an array and update the state accordingly
        if (response.data && Array.isArray(response.data)) {
            setHoliday((prev) => [...prev, ...response.data]); 
            return { success: true };
        } else {
            console.warn("Unexpected response format:", response.data);
        }
    } catch (error) {
      console.error("Error creating holiday in bulk:", error.response?.data?.message);
      setError(error.response?.data?.message || "Failed to create holiday in bulk.");
      return { success: false, message: error.response?.data?.message };
    }
  };
  

  // Memoize the context value
  const value = useMemo(
    () => ({
        holidays,
        isLoading,
        error,
        fetchHolidays,
        pagination,
        addHoliday,
        updateHoliday,
        deleteHoliday,
        createBulkHoliday
    }),
    [fetchHolidays, isLoading] // Recalculate the value only when `holidays` changes
  );

  return <HolidayMasterContext.Provider value={value}>{children}</HolidayMasterContext.Provider>;
};
